/**
 * PATH: packages/email-hub/src/feature/email-layout/EmailBottomDock.tsx
 * FILE_ID: EMAIL-LAYOUT-BOTTOM-DOCK-001
 * PURPOSE: Render the wide horizontal counterpart to the Email side rail.
 */

import {
  AlertCircle,
  Inbox,
  KanbanSquare,
  Loader2,
  PenSquare,
  RefreshCw,
} from 'lucide-react'
import { NavLink } from 'react-router-dom'
import { cn } from '../../_internal/lib/utils'
import { EmailBackgroundControl } from '../components/EmailBackgroundControl'
import { EMAIL_NAV_ITEMS } from '../config/navigation'
import { emailPath } from '../lib/basePath'
import { AddMailboxNavRow } from './AddMailboxNavRow'
import { formatCompact, formatRelativeTime } from './formatters'
import { LayoutControl } from './LayoutControl'
import type { EmailLayoutController } from './useEmailLayoutController'

function BottomFolderNavigation({
  layout,
}: {
  layout: EmailLayoutController
}) {
  return (
    <nav className="flex items-center gap-1 shrink-0">
      {EMAIL_NAV_ITEMS.map((item) => {
        const Icon = item.icon
        const count =
          item.id === 'inbox'
            ? layout.stats?.unread ?? 0
            : item.id === 'drafts'
              ? layout.draftCount
              : 0
        return (
          <NavLink
            key={item.id}
            to={emailPath(item.href.replace(/^\/email/, ''))}
            className={({ isActive }) =>
              cn(
                'relative flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs transition-colors shrink-0',
                isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )
            }
            title={item.label}
          >
            <Icon className="w-3.5 h-3.5" />
            <span>{item.label}</span>
            {count > 0 && (
              <span className="ml-0.5 text-[10px] bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 min-w-[18px] text-center font-medium leading-none">
                {count > 99 ? '99+' : count}
              </span>
            )}
          </NavLink>
        )
      })}
      {layout.emailAccounts.length === 0 && (
        <AddMailboxNavRow
          variant="pill"
          open={layout.addMailboxPickerOpen}
          setOpen={layout.setAddMailboxPickerOpen}
          onMicrosoft={() => {
            void layout.connectOutlook()
          }}
          onGoogle={() => {
            void layout.gmail.authorize()
          }}
        />
      )}
    </nav>
  )
}

function BottomSyncStatus({ layout }: { layout: EmailLayoutController }) {
  const liveSyncing =
    layout.isSyncing
    || !!layout.syncStatus?.active_sync
    || layout.backfill.running
  const summary = layout.syncStatus?.summary
  const oldestSeconds = summary?.oldest_sync_age_seconds ?? null
  const syncedAt =
    oldestSeconds != null
      ? Date.now() - oldestSeconds * 1000
      : layout.lastSyncAt
  const stale = oldestSeconds != null && oldestSeconds > 15 * 60
  const hasError =
    !!summary?.any_with_error
    || layout.syncStatus?.latest?.status === 'error'
  const relative = syncedAt ? formatRelativeTime(syncedAt) : null
  const showBackfill =
    !layout.backfill.state.complete && layout.backfill.progressPct > 0
  if (!relative && !liveSyncing && !showBackfill) return null

  const warn = (stale || hasError) && !liveSyncing
  const title = [
    relative ? `Last synced ${relative}` : 'No sync has completed yet',
    liveSyncing
      ? 'A sync is currently running'
      : 'Auto-sync runs every 15 minutes',
    !layout.backfill.state.complete
      ? `History backfill ${layout.backfill.progressPct}% - +${formatCompact(layout.backfill.state.total_created)} added so far`
      : layout.backfill.state.total_chunks > 0
        ? `History caught up · +${formatCompact(layout.backfill.state.total_created)} added`
        : '',
    hasError ? 'Last sync reported an error - open Recent issues' : '',
  ]
    .filter(Boolean)
    .join('\n')

  return (
    <button
      type="button"
      onClick={() => layout.setIssuesOpen(true)}
      className={cn(
        'flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs transition-colors shrink-0 max-w-[180px]',
        warn
          ? 'border-amber-500/40 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10'
          : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
      title={title}
      aria-label="Sync status - open details"
    >
      <span
        className="inline-flex items-center justify-center h-3 w-3 shrink-0"
        aria-hidden
      >
        {liveSyncing ? (
          <Loader2 className="h-2.5 w-2.5 animate-spin" />
        ) : (
          <span
            className={cn(
              'inline-block h-1.5 w-1.5 rounded-full',
              warn
                ? 'bg-amber-500'
                : relative
                  ? 'bg-emerald-500'
                  : 'bg-muted-foreground/40',
            )}
          />
        )}
      </span>
      <span className="truncate">
        {relative
          ? `Synced ${relative}`
          : liveSyncing
            ? 'Syncing…'
            : 'Auto-syncs'}
        {showBackfill && ` · ${layout.backfill.progressPct}%`}
      </span>
    </button>
  )
}

function IssueButton({ layout }: { layout: EmailLayoutController }) {
  const count =
    layout.issueErrorCount > 0
      ? layout.issueErrorCount
      : layout.issueWarningCount
  return (
    <button
      type="button"
      onClick={() => layout.setIssuesOpen(true)}
      className={cn(
        'relative flex items-center justify-center h-8 w-8 rounded-lg transition-colors shrink-0',
        layout.issueErrorCount > 0
          ? 'text-red-500 hover:bg-red-500/10'
          : layout.issueWarningCount > 0
            ? 'text-amber-500 hover:bg-amber-500/10'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
      title="Recent issues"
      aria-label="Recent issues"
    >
      <AlertCircle className="w-4 h-4" />
      {count > 0 && (
        <span
          className={cn(
            'absolute -top-0.5 -right-0.5 text-[9px] font-semibold rounded-full w-4 h-4 flex items-center justify-center leading-none',
            layout.issueErrorCount > 0
              ? 'bg-red-500 text-white'
              : 'bg-amber-500 text-black',
          )}
        >
          {count}
        </span>
      )}
    </button>
  )
}

export function EmailBottomDock({
  layout,
}: {
  layout: EmailLayoutController
}) {
  if (!layout.useBottomNav) return null

  return (
    <div className="order-2 shrink-0 border-t border-border bg-muted/30 flex items-center gap-1 px-2 h-12 overflow-x-auto">
      <button
        type="button"
        onClick={layout.openNewCompose}
        className="flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-xs font-medium hover:bg-primary/90 transition-colors shrink-0"
        title="Compose"
      >
        <PenSquare className="w-3.5 h-3.5" />
        <span>Compose</span>
      </button>
      <NavLink
        to="/kanban"
        className={({ isActive }) =>
          cn(
            'flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors shrink-0',
            isActive
              ? 'border-primary/40 bg-primary/10 text-primary'
              : 'border-border hover:bg-accent text-foreground',
          )
        }
        title="Switch to Kanban"
      >
        <KanbanSquare className="w-3.5 h-3.5" />
        <span>Kanban</span>
      </NavLink>
      <div className="h-6 w-px bg-border mx-1 shrink-0" />
      <BottomFolderNavigation layout={layout} />
      <div className="flex-1" />
      <BottomSyncStatus layout={layout} />
      {layout.emailAccounts.length > 0 && (
        <button
          ref={layout.mailboxesBtnRef}
          type="button"
          onClick={() =>
            layout.setMailboxesDockOpen(
              (open: boolean) => !open,
            )
          }
          className={cn(
            'flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs transition-colors shrink-0',
            layout.mailboxesDockOpen
              ? 'border-primary/40 bg-primary/10 text-primary'
              : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
          aria-haspopup="dialog"
          aria-expanded={layout.mailboxesDockOpen}
          title="Connected mailboxes"
        >
          <Inbox className="w-3.5 h-3.5" />
          <span>Mailboxes</span>
          <span className="text-[10px] opacity-70">
            {layout.emailAccounts.length}
          </span>
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          if (layout.outlookConnected && !layout.outlookExpired) {
            void layout.handleSync()
          } else {
            void layout.connectOutlook()
          }
        }}
        disabled={layout.isSyncing}
        className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs hover:bg-accent transition-colors disabled:opacity-50 shrink-0"
        title={
          layout.outlookConnected && !layout.outlookExpired
            ? layout.isSyncing
              ? 'Syncing…'
              : 'Sync Now'
            : 'Connect mailbox'
        }
      >
        {layout.isSyncing ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <RefreshCw className="w-3.5 h-3.5" />
        )}
        <span>{layout.isSyncing ? 'Syncing…' : 'Sync'}</span>
      </button>
      <IssueButton layout={layout} />
      <LayoutControl variant="bar" />
      <EmailBackgroundControl />
    </div>
  )
}
